/**
 * Formateo para pantalla. Puro y sin locale sorpresa: la federación lee
 * números, no notación científica.
 *
 * Los NÚMEROS no dependen del idioma (un porcentaje se lee igual en los tres);
 * las ETIQUETAS de fundamentos y razones sí, y salen del i18n.
 */
import { getT, type MessageKey, type TFunction } from '../i18n/index';

/** `0.4123` → `'41%'`. Ratios del contrato (0..1). */
export function pct(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(decimals)}%`;
}

/** Eficiencia con signo explícito: `-0.12` → `'−12%'`. */
export function effPct(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return '—';
  const n = value * 100;
  const sign = n < 0 ? '−' : '';
  return `${sign}${Math.abs(n).toFixed(decimals)}%`;
}

/** Entero con signo para el neto de rotación. */
export function signed(value: number): string {
  if (value > 0) return `+${value}`;
  if (value < 0) return `−${Math.abs(value)}`;
  return '0';
}

/** ISO → `dd/mm/aaaa hh:mm` (hora local del dirigente). */
export function dateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Claves i18n de los fundamentos. */
const SKILL_KEYS: Record<string, MessageKey> = {
  serve: 'skills.names.serve',
  reception: 'skills.names.reception',
  set: 'skills.names.set',
  attack: 'skills.names.attack',
  block: 'skills.names.block',
  dig: 'skills.names.dig',
  freeball: 'skills.names.freeball',
};

export function skillLabel(skill: string, t: TFunction = getT()): string {
  if (skill.startsWith('custom:')) return skill.slice('custom:'.length);
  const key = SKILL_KEYS[skill];
  return key ? t(key) : skill;
}

/** Claves i18n de las razones de punto (`point.reason`). */
const REASON_KEYS: Record<string, MessageKey> = {
  attack: 'skills.reasons.attack',
  block: 'skills.reasons.block',
  serve: 'skills.reasons.serve',
  opponent_error: 'skills.reasons.opponent_error',
  sanction: 'skills.reasons.sanction',
  // Los cinco del acta (§2.3). Sin ellos, un punto por demora o por la
  // reconciliación de un challenge se mostraba con su id técnico
  // (`delay_penalty`) en el resumen del partido y en el portal público.
  delay_penalty: 'skills.reasons.delay_penalty',
  illegal_substitution: 'skills.reasons.illegal_substitution',
  position_fault: 'skills.reasons.position_fault',
  challenge_adjustment: 'skills.reasons.challenge_adjustment',
  forfeit: 'skills.reasons.forfeit',
  other: 'skills.reasons.other',
};

export function reasonLabel(reason: string, t: TFunction = getT()): string {
  const key = REASON_KEYS[reason];
  return key ? t(key) : reason;
}

/** Camiseta + nombre, si el plantel lo declara. */
export function playerLabel(number: number, name?: string): string {
  return name && name.trim() !== '' ? `#${number} ${name}` : `#${number}`;
}

/** Orden estable de fundamentos para las tablas (el que lee un entrenador). */
export const SKILL_ORDER: readonly string[] = [
  'serve',
  'reception',
  'attack',
  'block',
  'dig',
  'set',
  'freeball',
];

/** Ordena las claves de `skills` con `SKILL_ORDER` y deja las custom al final. */
export function sortSkills(skills: Iterable<string>): string[] {
  return [...skills].sort((a, b) => {
    const ia = SKILL_ORDER.indexOf(a);
    const ib = SKILL_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}
